/* eslint-disable max-len */
import { TOptions } from 'i18next';
import React, { FC, lazy, memo, useMemo, useRef, useState } from 'react';
import { Route, Switch, useRouteMatch } from 'react-router-dom';

import { UiFullScreenLoader } from 'src/components/UI/loaders/FullScreenTransparent';
import { AdminTableEnum } from 'src/models/Administration/common';
import { CRoutes } from 'src/routes';
import { SafeAnyType } from 'src/utils/safeAny';
import { AdministrationInnerLayout } from './Campaigns/components/layout';

import 'src/styles/pages/administration/_administrationCommon.scss';

const adminPath = '/administration';
const adminPartnerPath = `${adminPath}/partner`;
const partnerAdminPath = '/partner';
const adminConfigurationPath = `${adminPath}/configuration`;
const adminIntegrationsPath = `${adminPath}/integrations`;

export const ROUTE_ADMINISTRATION_CAMPAIGNS_MANAGEMENT = `${adminPath}/campaigns`;
export const ROUTE_ADMINISTRATION_CAMPAIGNS_FUNDREQUESTS = `${ROUTE_ADMINISTRATION_CAMPAIGNS_MANAGEMENT}/fundrequests`;
export const ROUTE_ADMINISTRATION_AFFILIATES = `${adminPath}/affiliates`;
export const ROUTE_ADMINISTRATION_AFFILIATES_PAYMENTS = `${adminPath}/affiliates/payments`;
export const ROUTE_ADMINISTRATION_CAMPAIGNS_TRANSACTIONS = `${ROUTE_ADMINISTRATION_CAMPAIGNS_MANAGEMENT}/contributions`;
export const ROUTE_ADMINISTRATION_IMPORT_DONATIONS = `${adminPath}/importdonations/importdonations`;
export const ROUTE_ADMINISTRATION_CAMPAIGNS_EXPORT_DONATIONS = `${ROUTE_ADMINISTRATION_CAMPAIGNS_MANAGEMENT}/exportdonations`;
export const ROUTE_ADMINISTRATION_ORGANIZATIONS_MANAGEMENT = `${adminPath}/organizations`;
export const ROUTE_ADMINISTRATION_ERROR_LOGS_PAGE = `${adminPath}/errorlogs`;
export const ROUTE_ADMINISTRATION_AUDIT_PAGE = `${adminConfigurationPath}/audit`;
export const ROUTE_ADMINISTRATION_PAYMENT_APP_PAGE = `${adminConfigurationPath}/paymentApplication`;
export const ROUTE_ADMINISTRATION_ORGANIZATIONS_ADMINISTRATORS = `${ROUTE_ADMINISTRATION_ORGANIZATIONS_MANAGEMENT}/administrators/:permanenturl`;
export const ROUTE_ADMINISTRATION_PARTNERS_MANAGEMENT = `${adminPartnerPath}/list`;
export const ROUTE_ADMINISTRATION_CONFIGURATION_USERS = `${adminConfigurationPath}/users`;
export const ROUTE_ADMINISTRATION_CONFIGURATION_SUBSCRIPTION_PLANS = `${adminPath}/subscriptionplans`;
export const ROUTE_ADMINISTRATION_CONFIGURATION_PROMO_CODES = `${adminConfigurationPath}/organizerpromocodes`;
export const ROUTE_ADMINISTRATION_CONFIGURATION_FEES = `${adminConfigurationPath}/fees`;
export const ROUTE_ADMINISTRATION_CONFIGURATION_RAFFLE_SWEEPS_TIP = `${adminConfigurationPath}/rafflesweepstip`;
export const ROUTE_ADMINISTRATION_INTEGRATIONS_SYSTEM = `${adminIntegrationsPath}/systems`;
export const ROUTE_ADMINISTRATION_INTEGRATIONS_REST_API = `${adminIntegrationsPath}/restapi`;
export const ROUTE_ADMINISTRATION_CONFIGURATION_FUNDING = `${adminPath}/internalprocessingconfiguration/administrationfunding`;
export const ROUTE_ADMINISTRATION_CONFIGURATION_PLEDGE_FUNDING = `${adminPath}/PledgePaymentProcessingConfiguration/AdministrationFunding`;
export const ROUTE_ADMINISTRATION_PARTNER_ADMINISTRATORS = `${adminPath}/partneradministrators/:partnerId`;
export const ROUTE_ADMINISTRATION_CAMPAIGNS_MANAGEMENT_REQUEST_DETAILS = `${adminPath}/campaigns/requestdetails/:id`;
export const ROUTE_ADMINISTRATION_TRANSACTIONS_CHARGEBACKS_LIST = `${adminPath}/campaigns/chargebacks/:id`;
export const ROUTE_ADMINISTRATION_PARTNERS_SETUP = `${adminPartnerPath}/setup/:partnerId?`;
export const ROUTE_ADMINISTRATION_PARTNERS_SPLIT_EXCEPTIONS = `${adminPartnerPath}/splitexceptions/:partnerId`;
export const ROUTE_ADMINISTRATION_EXPERIENCE_TEMPLATES = `${adminPath}/campaignTemplates`;
export const ROUTE_ADMINISTRATION_MANAGED_CAMPAIGNS = `${adminPath}/managedCampaigns`;

/** define partner administration pages routes */
export const ROUTE_PARTNER_ADMINISTRATION_CAMPAIGNS_MANAGEMENENT = `${partnerAdminPath}/campaigns`;
export const ROUTE_PARTNER_ADMINISTRATION_ORGANIZATIONS_LIST = `${partnerAdminPath}/dashboardpartnerorganizations/organizations`;
export const ROUTE_PARTNER_ADMINISTRATION_ORGANIZATIONS_ADMINISTRATORS_LIST = `${partnerAdminPath}/organizationadministrators/:permanentUrl?`;
export const ROUTE_PARTNER_ADMINISTRATION_TRANSACTIONS_LIST = `${partnerAdminPath}/contributions`;
export const ROUTE_PARTNER_ADMINISTRATION_TRANSACTIONS_CHARGEBACKS_LIST = `${partnerAdminPath}/dashboardpartnercontributions/chargebacks/:id`;
export const ROUTE_PARTNER_ADMINISTRATION_USERS_LIST = `${partnerAdminPath}/users`;
export const ROUTE_PARTNER_ADMINISTRATION_ADMINISTRATORS_LIST = `${partnerAdminPath}/administrators`;
export const ROUTE_PARTNER_ADMINISTRATION_API_LIST = `${partnerAdminPath}/api`;
export const ROUTE_PARTNER_ADMINISTRATION_WEBHOOKS_PAGE = `${partnerAdminPath}/webhooks`;
export const ROUTE_PARTNER_ADMINISTRATION_FUNDING_PAGE = `${partnerAdminPath}/funding`;
export const ROUTE_PARTNER_ADMINISTRATION_EXPERIENCE_TEMPLATES_PAGE = `${partnerAdminPath}/DashboardPartnerCampaignTemplates`;
export const ROUTE_PARTNER_ADMINISTRATION_AUDIT_PAGE = `${partnerAdminPath}/DashboardPartnerCampaignsAudit/audit`;

/** generate links */
export const getLinkRouteAdministrationPartnersSplitExceptions = (url: string | number) =>
  ROUTE_ADMINISTRATION_PARTNERS_SPLIT_EXCEPTIONS.replace(':partnerId', url.toString());

export const getLinkRouteAdministrationPartnersAdministrators = (url: string | number) =>
  ROUTE_ADMINISTRATION_PARTNER_ADMINISTRATORS.replace(':partnerId', url.toString());

export const getLinkRouteAdministrationPartnersSetup = (url: string | number) =>
  ROUTE_ADMINISTRATION_PARTNERS_SETUP.replace(':partnerId', url.toString());

/** import pages */
const AdministrationCampaignsManagementList = lazy(() => import('src/pages/Administration/Campaigns/Management'));
const AdministrationErrorLogsPage = lazy(() => import('src/pages/Administration/Configurations/ErrorLogs'));
const AdministrationCampaignsFundRequestsList = lazy(() => import('src/pages/Administration/Campaigns/FundRequests'));
const AdministrationAffiliatesList = lazy(() => import('src/pages/Administration/Affiliates'));
const AdministrationAffiliatesPaymentsList = lazy(() => import('src/pages/Administration/Affiliates/payments'));
const AdministrationCampaignsTransactionsList = lazy(() => import('src/pages/Administration/Campaigns/Transactions'));
const AdministrationImportDonations = lazy(() => import('src/pages/Administration/ImportDonations'));
const AdministrationExportDonations = lazy(() => import('src/pages/Administration/Campaigns/ExportDonations'));
const AdministrationOrganizationsManagementList = lazy(
  () => import('src/pages/Administration/Organizations/Management')
);
const AdministrationPartnersManagementList = lazy(() => import('src/pages/Administration/Partners/Management'));
const AdministrationConfigurationUsersList = lazy(() => import('src/pages/Administration/Configurations/Users'));
const AdministrationConfigurationSubscriptionPlansList = lazy(
  () => import('src/pages/Administration/Configurations/SubscriptionPlans')
);
const AdministrationConfigurationPromoCodesList = lazy(
  () => import('src/pages/Administration/Configurations/PromoCodes')
);
const AdministrationConfigurationFeesList = lazy(() => import('src/pages/Administration/Configurations/Fees'));
const AdministrationConfigurationRaffleSweepsTip = lazy(
  () => import('src/pages/Administration/Configurations/RaffleSweepsTip')
);
const AdministrationIntegrationsSystemList = lazy(() => import('src/pages/Administration/Integrations/Systems'));
const AdministrationIntegrationsRestApiList = lazy(() => import('src/pages/Administration/Integrations/RestApi'));
const AdministrationFundingPage = lazy(() => import('src/pages/Administration/Configurations/Funding'));
const AdministrationPledgeFundingPage = lazy(() => import('src/pages/Administration/Configurations/PledgeFunding'));
const AdministrationPartnerAdministratorsList = lazy(() => import('src/pages/Administration/PartnerAdministrators'));
const AdministrationCampaignsRequestDetailsPage = lazy(
  () => import('src/pages/Administration/Campaigns/RequestDetails')
);
const AdministrationPartnerSetup = lazy(() => import('src/pages/Administration/Partners/Setup'));
const AdministrationPartnersSplitExceptionsList = lazy(
  () => import('src/pages/Administration/Partners/SplitExceptions')
);
const AdministrationOrganizationsAdministratorsList = lazy(
  () => import('src/pages/Administration/Organizations/Administrators')
);
const PartnerAdministrationTransactionsChargebacksList = lazy(
  () => import('src/pages/Partner/ContributionsChargebacks')
);
const AdministrationExperienceTemplatesPage = lazy(
  () => import('src/pages/Administration/Campaigns/ExperienceTemplates')
);
const AdministrationAuditPage = lazy(() => import('src/pages/Administration/Configurations/Audit'));

const PaymentAppPage = lazy(() => import('src/pages/Administration/Configurations/PaymentApplication'));

const AdministrationManagedCampaignsList = lazy(() => import('src/pages/Administration/Campaigns/ManagedCampaigns'));

// partner administration pages
const PartnerAdministrationCampaignsList = lazy(() => import('src/pages/Partner/Campaigns'));
const PartnerAdministrationOrganizationsList = lazy(() => import('src/pages/Partner/Organizations'));
const PartnerAdministrationOrganizationsAdministratorsList = lazy(
  () => import('src/pages/Partner/Organizations/Administrators')
);
const PartnerAdministrationTransactionsList = lazy(() => import('src/pages/Partner/Transactions'));
const PartnerAdministrationUsers = lazy(() => import('src/pages/Partner/Users'));
const PartnerAdministrationAdministratorsList = lazy(() => import('src/pages/Partner/Administrators'));
const PartnerAdministrationApiList = lazy(() => import('src/pages/Partner/Api'));
const PartnerAdministrationWebhookList = lazy(() => import('src/pages/Partner/Webhooks'));
const PartnerAdministrationFunding = lazy(() => import('src/pages/Partner/Funding'));

/** export routes */
export const administrationRoutes: CRoutes = [
  {
    path: ROUTE_ADMINISTRATION_CAMPAIGNS_MANAGEMENT,
    Component: AdministrationCampaignsManagementList,
  },
  {
    path: ROUTE_ADMINISTRATION_CAMPAIGNS_FUNDREQUESTS,
    Component: AdministrationCampaignsFundRequestsList,
  },
  {
    path: ROUTE_ADMINISTRATION_AFFILIATES,
    Component: AdministrationAffiliatesList,
  },
  {
    path: ROUTE_ADMINISTRATION_AFFILIATES_PAYMENTS,
    Component: AdministrationAffiliatesPaymentsList,
  },
  {
    path: ROUTE_ADMINISTRATION_CAMPAIGNS_TRANSACTIONS,
    Component: AdministrationCampaignsTransactionsList,
  },
  {
    path: ROUTE_ADMINISTRATION_IMPORT_DONATIONS,
    Component: AdministrationImportDonations,
  },
  {
    path: ROUTE_ADMINISTRATION_CAMPAIGNS_EXPORT_DONATIONS,
    Component: AdministrationExportDonations,
  },
  {
    path: ROUTE_ADMINISTRATION_ORGANIZATIONS_MANAGEMENT,
    Component: AdministrationOrganizationsManagementList,
  },
  {
    path: ROUTE_ADMINISTRATION_PARTNERS_MANAGEMENT,
    Component: AdministrationPartnersManagementList,
  },
  {
    path: ROUTE_ADMINISTRATION_CONFIGURATION_USERS,
    Component: AdministrationConfigurationUsersList,
  },
  {
    path: ROUTE_ADMINISTRATION_CONFIGURATION_SUBSCRIPTION_PLANS,
    Component: AdministrationConfigurationSubscriptionPlansList,
  },
  {
    path: ROUTE_ADMINISTRATION_CONFIGURATION_PROMO_CODES,
    Component: AdministrationConfigurationPromoCodesList,
  },
  {
    path: ROUTE_ADMINISTRATION_CONFIGURATION_FEES,
    Component: AdministrationConfigurationFeesList,
  },
  {
    path: ROUTE_ADMINISTRATION_CONFIGURATION_RAFFLE_SWEEPS_TIP,
    Component: AdministrationConfigurationRaffleSweepsTip,
  },
  {
    path: ROUTE_ADMINISTRATION_INTEGRATIONS_SYSTEM,
    Component: AdministrationIntegrationsSystemList,
  },
  {
    path: ROUTE_ADMINISTRATION_INTEGRATIONS_REST_API,
    Component: AdministrationIntegrationsRestApiList,
  },
  {
    path: ROUTE_ADMINISTRATION_CONFIGURATION_FUNDING,
    Component: AdministrationFundingPage,
  },
  {
    path: ROUTE_ADMINISTRATION_CONFIGURATION_PLEDGE_FUNDING,
    Component: AdministrationPledgeFundingPage,
  },
  {
    path: ROUTE_ADMINISTRATION_PARTNER_ADMINISTRATORS,
    Component: AdministrationPartnerAdministratorsList,
  },
  {
    path: ROUTE_ADMINISTRATION_CAMPAIGNS_MANAGEMENT_REQUEST_DETAILS,
    Component: AdministrationCampaignsRequestDetailsPage,
  },
  {
    path: ROUTE_ADMINISTRATION_TRANSACTIONS_CHARGEBACKS_LIST,
    Component: () => <PartnerAdministrationTransactionsChargebacksList partner={false} />,
  },
  {
    path: ROUTE_ADMINISTRATION_PARTNERS_SETUP,
    Component: AdministrationPartnerSetup,
  },
  {
    path: ROUTE_ADMINISTRATION_PARTNERS_SPLIT_EXCEPTIONS,
    Component: AdministrationPartnersSplitExceptionsList,
  },
  {
    path: ROUTE_ADMINISTRATION_EXPERIENCE_TEMPLATES,
    Component: () => <AdministrationExperienceTemplatesPage partner={false} />,
  },
  {
    path: ROUTE_ADMINISTRATION_AUDIT_PAGE,
    Component: () => <AdministrationAuditPage partner={false} />,
  },
  {
    path: ROUTE_ADMINISTRATION_PAYMENT_APP_PAGE,
    Component: PaymentAppPage,
  },
  {
    path: ROUTE_ADMINISTRATION_ORGANIZATIONS_ADMINISTRATORS,
    Component: AdministrationOrganizationsAdministratorsList,
  },
  {
    path: ROUTE_ADMINISTRATION_ERROR_LOGS_PAGE,
    Component: AdministrationErrorLogsPage,
  },
  {
    path: ROUTE_ADMINISTRATION_MANAGED_CAMPAIGNS,
    Component: AdministrationManagedCampaignsList,
  },
  {
    path: ROUTE_PARTNER_ADMINISTRATION_CAMPAIGNS_MANAGEMENENT,
    Component: PartnerAdministrationCampaignsList,
  },
  {
    path: ROUTE_PARTNER_ADMINISTRATION_ORGANIZATIONS_LIST,
    Component: PartnerAdministrationOrganizationsList,
  },
  {
    path: ROUTE_PARTNER_ADMINISTRATION_ORGANIZATIONS_ADMINISTRATORS_LIST,
    Component: PartnerAdministrationOrganizationsAdministratorsList,
  },
  {
    path: ROUTE_PARTNER_ADMINISTRATION_TRANSACTIONS_LIST,
    Component: PartnerAdministrationTransactionsList,
  },
  {
    path: ROUTE_PARTNER_ADMINISTRATION_TRANSACTIONS_CHARGEBACKS_LIST,
    Component: PartnerAdministrationTransactionsChargebacksList,
  },
  {
    path: ROUTE_PARTNER_ADMINISTRATION_USERS_LIST,
    Component: PartnerAdministrationUsers,
  },
  {
    path: ROUTE_PARTNER_ADMINISTRATION_ADMINISTRATORS_LIST,
    Component: PartnerAdministrationAdministratorsList,
  },
  {
    path: ROUTE_PARTNER_ADMINISTRATION_API_LIST,
    Component: PartnerAdministrationApiList,
  },
  {
    path: ROUTE_PARTNER_ADMINISTRATION_WEBHOOKS_PAGE,
    Component: PartnerAdministrationWebhookList,
  },
  {
    path: ROUTE_PARTNER_ADMINISTRATION_FUNDING_PAGE,
    Component: PartnerAdministrationFunding,
  },
  {
    path: ROUTE_PARTNER_ADMINISTRATION_EXPERIENCE_TEMPLATES_PAGE,
    Component: () => <AdministrationExperienceTemplatesPage partner />,
  },
  {
    path: ROUTE_PARTNER_ADMINISTRATION_AUDIT_PAGE,
    Component: () => <AdministrationAuditPage partner />,
  },
].map((routeInfo) => ({ ...routeInfo, layout: 'administration' }));

type AdministrationContextType = {
  changeHeaderLocaleOptions: (localeOptions?: TOptions<Record<string, SafeAnyType>>) => void;
};

export const AdministrationContext = React.createContext<AdministrationContextType>({
  changeHeaderLocaleOptions: () => null,
});
AdministrationContext.displayName = 'AdministrationContext';

export const AdministrationPages: FC = memo(() => {
  const [localeOptions, setTocaleOptions] =
    useState<Parameters<AdministrationContextType['changeHeaderLocaleOptions']>[0]>(undefined);

  const routes = useRef([
    {
      path: ROUTE_ADMINISTRATION_CAMPAIGNS_MANAGEMENT,
      type: AdminTableEnum.CAMPAIGNS_MANAGEMENT,
    },
    {
      path: ROUTE_ADMINISTRATION_CAMPAIGNS_FUNDREQUESTS,
      type: AdminTableEnum.CAMPAIGNS_FUNDREQUESTS,
    },
    {
      path: ROUTE_ADMINISTRATION_AFFILIATES,
      type: AdminTableEnum.AFFILIATES_INDEX,
    },
    {
      path: ROUTE_ADMINISTRATION_CAMPAIGNS_TRANSACTIONS,
      type: AdminTableEnum.CAMPAIGNS_TRANSACTIONS,
    },
    {
      path: ROUTE_ADMINISTRATION_IMPORT_DONATIONS,
      type: AdminTableEnum.IMPORT_DONATIONS,
    },
    {
      path: ROUTE_ADMINISTRATION_CAMPAIGNS_EXPORT_DONATIONS,
      type: AdminTableEnum.CAMPAIGNS_EXPORT_DONATIONS,
    },
    {
      path: ROUTE_ADMINISTRATION_ORGANIZATIONS_MANAGEMENT,
      type: AdminTableEnum.ORGANIZATIONS_MANAGEMENT,
    },
    {
      path: ROUTE_ADMINISTRATION_PARTNERS_MANAGEMENT,
      type: AdminTableEnum.PARTNERS_MANAGEMENT,
    },
    {
      path: ROUTE_ADMINISTRATION_CONFIGURATION_USERS,
      type: AdminTableEnum.CONFIGURATION_USERS,
    },
    {
      path: ROUTE_ADMINISTRATION_CONFIGURATION_SUBSCRIPTION_PLANS,
      type: AdminTableEnum.CONFIGURATION_SUBSCRIPTIONS_PLANS,
    },
    {
      path: ROUTE_ADMINISTRATION_CONFIGURATION_PROMO_CODES,
      type: AdminTableEnum.CONFIGURATION_PROMO_CODES,
    },
    {
      path: ROUTE_ADMINISTRATION_CONFIGURATION_FEES,
      type: AdminTableEnum.CONFIGURATION_FEES,
    },
    {
      path: ROUTE_ADMINISTRATION_PAYMENT_APP_PAGE,
      type: AdminTableEnum.CONFIGURATION_PAYMENT_APP,
    },
    {
      path: ROUTE_ADMINISTRATION_CONFIGURATION_RAFFLE_SWEEPS_TIP,
      type: AdminTableEnum.CONFIGURATION_RAFFLE_SWEEPS_TIP,
    },
    {
      path: ROUTE_ADMINISTRATION_INTEGRATIONS_SYSTEM,
      type: AdminTableEnum.INTEGRATIONS_SYSTEM,
    },
    {
      path: ROUTE_ADMINISTRATION_INTEGRATIONS_REST_API,
      type: AdminTableEnum.INTEGRATIONS_REST_API,
    },
    {
      path: ROUTE_ADMINISTRATION_CONFIGURATION_FUNDING,
      type: AdminTableEnum.CONFIGURATION_FUNDING,
    },
    {
      path: ROUTE_ADMINISTRATION_CONFIGURATION_PLEDGE_FUNDING,
      type: AdminTableEnum.CONFIGURATION_PLEDGE_FUNDING,
    },
    {
      path: ROUTE_ADMINISTRATION_PARTNER_ADMINISTRATORS,
      type: AdminTableEnum.PARTNER_ADMINISTRATORS,
    },
    {
      path: ROUTE_ADMINISTRATION_CAMPAIGNS_MANAGEMENT_REQUEST_DETAILS,
      type: AdminTableEnum.CAMPAIGNS_REQUEST_DETAILS,
    },
    {
      path: ROUTE_ADMINISTRATION_TRANSACTIONS_CHARGEBACKS_LIST,
      type: AdminTableEnum.CAMPAIGNS_TRANSACTIONS_CHARGEBACKS,
    },
    {
      path: ROUTE_ADMINISTRATION_ERROR_LOGS_PAGE,
      type: AdminTableEnum.CONFIGURATION_ERROR_LOGS,
    },
    {
      path: ROUTE_ADMINISTRATION_PARTNERS_SETUP,
      type: AdminTableEnum.PARTNERS_SETUP,
    },
    {
      path: ROUTE_ADMINISTRATION_PARTNERS_SPLIT_EXCEPTIONS,
      type: AdminTableEnum.PARTNERS_SPLIT_EXCEPTIONS,
    },
    {
      path: ROUTE_ADMINISTRATION_EXPERIENCE_TEMPLATES,
      type: AdminTableEnum.EXPERIENCE_TEMPLATES,
    },
    {
      path: ROUTE_ADMINISTRATION_ORGANIZATIONS_ADMINISTRATORS,
      type: AdminTableEnum.ORGANIZATIONS_ADMINISTRATORS,
    },
    {
      path: ROUTE_ADMINISTRATION_AUDIT_PAGE,
      type: AdminTableEnum.CONFIGURATION_AUDIT,
    },
    {
      path: ROUTE_ADMINISTRATION_MANAGED_CAMPAIGNS,
      type: AdminTableEnum.MANAGED_CAMPAIGNS,
    },

    {
      path: ROUTE_PARTNER_ADMINISTRATION_CAMPAIGNS_MANAGEMENENT,
      type: AdminTableEnum.ADMIN_PARTNER_CAMPAIGNS,
    },
    {
      path: ROUTE_PARTNER_ADMINISTRATION_ORGANIZATIONS_LIST,
      type: AdminTableEnum.ADMIN_PARTNER_ORGANIZATIONS,
    },
    {
      path: ROUTE_PARTNER_ADMINISTRATION_ORGANIZATIONS_ADMINISTRATORS_LIST,
      type: AdminTableEnum.ADMIN_PARTNER_ORGANIZATIONS_ADMINISTRATORS,
    },
    {
      path: ROUTE_PARTNER_ADMINISTRATION_TRANSACTIONS_LIST,
      type: AdminTableEnum.ADMIN_PARTNER_TRANSACTIONS,
    },
    {
      path: ROUTE_PARTNER_ADMINISTRATION_TRANSACTIONS_CHARGEBACKS_LIST,
      type: AdminTableEnum.ADMIN_PARTNER_TRANSACTIONS_CHARGEBACKS,
    },
    {
      path: ROUTE_PARTNER_ADMINISTRATION_USERS_LIST,
      type: AdminTableEnum.ADMIN_PARTNER_USERS,
    },
    {
      path: ROUTE_PARTNER_ADMINISTRATION_ADMINISTRATORS_LIST,
      type: AdminTableEnum.ADMIN_PARTNER_ADMINISTRATORS,
    },
    {
      path: ROUTE_PARTNER_ADMINISTRATION_API_LIST,
      type: AdminTableEnum.ADMIN_PARTNER_API,
    },
    {
      path: ROUTE_PARTNER_ADMINISTRATION_WEBHOOKS_PAGE,
      type: AdminTableEnum.ADMIN_PARTNER_WEBHOOKS,
    },
    {
      path: ROUTE_PARTNER_ADMINISTRATION_FUNDING_PAGE,
      type: AdminTableEnum.ADMIN_PARTNER_FUNDING,
    },
    {
      path: ROUTE_PARTNER_ADMINISTRATION_EXPERIENCE_TEMPLATES_PAGE,
      type: AdminTableEnum.ADMIN_PARTNER_EXPERIENCE_TEMPLATES,
    },
    {
      path: ROUTE_PARTNER_ADMINISTRATION_AUDIT_PAGE,
      type: AdminTableEnum.ADMIN_PARTNER_AUDIT,
    },
  ]);

  const match = useRouteMatch({ path: routes.current.map(({ path }) => path), exact: true });

  const type: AdminTableEnum = useMemo(
    () => routes.current.find((item) => item.path === match?.path)?.type || AdminTableEnum.AFFILIATES_PAYMENTS,
    [match?.path]
  );

  const contextValue: AdministrationContextType = useMemo(
    () => ({
      changeHeaderLocaleOptions: setTocaleOptions,
    }),
    []
  );

  return (
    <AdministrationContext.Provider value={contextValue}>
      <AdministrationInnerLayout type={type} localeOptions={localeOptions}>
        <React.Suspense fallback={<UiFullScreenLoader transparent useText={false} />}>
          <Switch>
            {administrationRoutes.map(({ path, Component }) => (
              <Route key={path.toString()} path={path} exact>
                <Component />
              </Route>
            ))}
          </Switch>
        </React.Suspense>
      </AdministrationInnerLayout>
    </AdministrationContext.Provider>
  );
});
